// Profile Store - Manages user profile state with real API integration
// Implements Phase 4 Task 4-01: ProfileStore with Real API Integration
//
// ARCH NOTE: This store intentionally has NO client-side default profiles and
// NO localStorage fallbacks for the active profile.  The backend is the single
// source of truth.  If the backend returns activeProfileId: null it means no
// profile has been seeded/activated, and the router will redirect to /onboarding.
// This makes the seed_system_config.py Docker Compose service the operator-level
// toggle: run it → seeded profile → dashboard; skip it → no profile → onboarding.

import { create } from 'zustand';

// Types matching Spec 12 §9.3.1
export type RiskLevel = 'conservative' | 'balanced' | 'aggressive';

export interface ProfileSettings {
  max_drawdown_pct: number;
  max_gross_exposure_pct: number;
  max_net_exposure_pct: number;
  max_position_pct: number;
  max_daily_trades: number;
  allowed_strategies: string[];
  forbidden_assets: string[];
  strategy_allocation: {
    carry_engine: number;
    trend_following: number;
    mean_reversion: number;
  };
}

export interface Profile {
  id: string;
  name: string;
  risk_level: RiskLevel;
  description: string;
  is_default: boolean;
  settings: ProfileSettings;
  created_at: string;
  updated_at: string;
}

export interface ProfileState {
  // State
  profiles: Profile[];
  activeProfileId: string | null;
  activeProfile: Profile | null;
  isLoading: boolean;
  isLoaded: boolean;
  /** True when the API call itself failed (network error, 5xx, etc.).
   *  Distinct from activeProfile === null which means "no profile seeded". */
  isApiError: boolean;
  error: string | null;

  // Actions
  fetchProfiles: () => Promise<void>;
  setActiveProfile: (profileId: string) => Promise<void>;
  createProfile: (profile: Partial<Profile>) => Promise<Profile>;
  updateProfile: (profileId: string, updates: Partial<Profile>) => Promise<void>;
  deleteProfile: (profileId: string) => Promise<void>;
  clearError: () => void;
}

// API base URL — use relative URLs so requests go through the nginx reverse proxy.
// Never fall back to an absolute localhost URL; that breaks Docker / staging / prod.
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// API helper
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  const token = localStorage.getItem('auth_token');

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.error?.message || `HTTP error ${response.status}`);
  }

  return response.json();
}

export const useProfileStore = create<ProfileState>()(
  (set, get) => ({
    // Initial state — no assumptions, no persisted cache
    profiles: [],
    activeProfileId: null,
    activeProfile: null,
    isLoading: false,
    isLoaded: false,
    isApiError: false,
    error: null,

    // Fetch all profiles from API — backend is the single source of truth
    fetchProfiles: async () => {
      set({ isLoading: true, error: null, isApiError: false });

      try {
        const response = await apiRequest<{ profiles: Profile[]; activeProfileId: string | null }>(
          '/api/profiles'
        );

        // Trust the backend completely — no client-side defaults or fallbacks.
        // If activeProfileId is null it means no profile is seeded; the router
        // will redirect to /onboarding.
        const profiles = response.profiles ?? [];
        let activeProfileId = response.activeProfileId ?? null;
        let activeProfile =
          activeProfileId !== null
            ? (profiles.find((p) => p.id === activeProfileId) ?? null)
            : null;

        // Check for pending profile from onboarding (set when backend was unavailable)
        const pendingProfileId = localStorage.getItem('tradeos_pending_profile');
        if (pendingProfileId && !activeProfileId) {
          // Try to activate the pending profile now that backend is available
          try {
            await apiRequest<{ ok: boolean }>(
              `/api/profiles/${pendingProfileId}/activate`,
              { method: 'POST' }
            );
            activeProfileId = pendingProfileId;
            activeProfile = profiles.find((p) => p.id === pendingProfileId) ?? null;
            console.log('Successfully synced pending profile:', pendingProfileId);
          } catch (syncErr) {
            console.warn('Failed to sync pending profile:', syncErr);
          }
          // Clear the pending profile regardless of success/failure
          localStorage.removeItem('tradeos_pending_profile');
        }

        set({
          profiles,
          activeProfileId,
          activeProfile,
          isLoading: false,
          isLoaded: true,
          isApiError: false,
        });
      } catch (error) {
        console.error('Failed to fetch profiles:', error);

        // API error (network down, 5xx, etc.) — mark as API error so the router
        // can show an error screen instead of redirecting to /onboarding.
        // We do NOT fall back to any persisted or hardcoded profile.
        set({
          profiles: [],
          activeProfileId: null,
          activeProfile: null,
          isLoading: false,
          isLoaded: true,
          isApiError: true,
          error: error instanceof Error ? error.message : 'Failed to fetch profiles',
        });
      }
    },

    // Set active profile
    setActiveProfile: async (profileId: string) => {
      set({ isLoading: true, error: null });

      try {
        await apiRequest<{ ok: boolean }>(
          `/api/profiles/${profileId}/activate`,
          { method: 'POST' }
        );

        const profile = get().profiles.find((p) => p.id === profileId);

        set({
          activeProfileId: profileId,
          activeProfile: profile || null,
          isLoading: false,
        });
      } catch (error) {
        console.error('Failed to activate profile:', error);
        set({
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to activate profile',
        });
        throw error;
      }
    },

    // Create a new custom profile
    createProfile: async (profileData: Partial<Profile>) => {
      set({ isLoading: true, error: null });

      try {
        const newProfile = await apiRequest<Profile>('/api/profiles', {
          method: 'POST',
          body: JSON.stringify(profileData),
        });

        set((state) => ({
          profiles: [...state.profiles, newProfile],
          isLoading: false,
        }));

        return newProfile;
      } catch (error) {
        console.error('Failed to create profile:', error);
        set({
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to create profile',
        });
        throw error;
      }
    },

    // Update an existing profile
    updateProfile: async (profileId: string, updates: Partial<Profile>) => {
      set({ isLoading: true, error: null });

      try {
        const updatedProfile = await apiRequest<Profile>(
          `/api/profiles/${profileId}`,
          {
            method: 'PUT',
            body: JSON.stringify(updates),
          }
        );

        set((state) => {
          const profiles = state.profiles.map((p) =>
            p.id === profileId ? updatedProfile : p
          );
          const activeProfile = state.activeProfileId === profileId
            ? updatedProfile
            : state.activeProfile;

          return { profiles, activeProfile, isLoading: false };
        });
      } catch (error) {
        console.error('Failed to update profile:', error);
        set({
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to update profile',
        });
        throw error;
      }
    },

    // Delete a custom profile
    deleteProfile: async (profileId: string) => {
      set({ isLoading: true, error: null });

      try {
        await apiRequest<void>(`/api/profiles/${profileId}`, {
          method: 'DELETE',
        });

        set((state) => ({
          profiles: state.profiles.filter((p) => p.id !== profileId),
          activeProfileId: state.activeProfileId === profileId
            ? null
            : state.activeProfileId,
          activeProfile: state.activeProfileId === profileId
            ? null
            : state.activeProfile,
          isLoading: false,
        }));
      } catch (error) {
        console.error('Failed to delete profile:', error);
        set({
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to delete profile',
        });
        throw error;
      }
    },

    // Clear error
    clearError: () => set({ error: null, isApiError: false }),
  })
);

// Selector hooks
export const useProfiles = () => useProfileStore((state) => state.profiles);
export const useActiveProfile = () => useProfileStore((state) => state.activeProfile);
export const useActiveProfileId = () => useProfileStore((state) => state.activeProfileId);
export const useProfileLoading = () => useProfileStore((state) => state.isLoading);
export const useProfileLoaded = () => useProfileStore((state) => state.isLoaded);
export const useProfileError = () => useProfileStore((state) => state.error);
export const useProfileApiError = () => useProfileStore((state) => state.isApiError);

export default useProfileStore;
